import axios from 'axios'
import { temporaryToken } from '@/config/auth'
import { apiBaseURL } from '@/config/host'

const service = axios.create({
  baseURL: apiBaseURL,
  timeout: 10000
})

const appendFormData = (formData, key, value) => {
  if (value === undefined || value === null || value === '') return

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      if (item && typeof item === 'object' && !(item instanceof File)) {
        Object.keys(item).forEach((childKey) => {
          appendFormData(formData, `${key}[${index}][${childKey}]`, item[childKey])
        })
      } else {
        appendFormData(formData, `${key}[${index}]`, item)
      }
    })
    return
  }

  formData.append(key, value)
}

const toFormData = (data = {}) => {
  const formData = new FormData()
  Object.keys(data).forEach((key) => {
    appendFormData(formData, key, data[key])
  })
  return formData
}

const pickValue = (source, keys) => {
  if (!source || typeof source !== 'object') return ''
  for (let i = 0; i < keys.length; i += 1) {
    const value = source[keys[i]]
    if (value !== undefined && value !== null && value !== '') return value
  }
  return ''
}

const getPayload = (response) => (
  response && response.data !== undefined ? response.data : response
)

const getAccessoryOrderId = (response) => {
  const payload = getPayload(response)
  const candidates = [
    payload,
    payload && payload.order,
    payload && payload.accessory_order,
    payload && payload.detail,
    payload && payload.info,
    payload && payload.data
  ]

  for (let i = 0; i < candidates.length; i += 1) {
    const orderId = pickValue(candidates[i], ['accessory_order_id', 'order_id', 'id'])
    if (orderId) return orderId
  }
  return ''
}

const splitAccessoryOrderPaymentData = (data = {}) => {
  const {
    payment_method = 1,
    payment_channel,
    payer_name,
    offline_remark,
    paid_at,
    ...orderData
  } = data

  return {
    orderData,
    paymentData: {
      payment_method,
      payment_channel,
      payer_name,
      offline_remark,
      paid_at
    }
  }
}

service.interceptors.request.use(
  config => {
    const token = temporaryToken || localStorage.getItem('token')
    if (token) {
      config.headers['token'] = token
      config.headers['Authorization'] = 'Bearer ' + token
      if (/\/v2\/member\/mall\/orders\/(main|accessory)\//.test(config.url || '')) {
        if (config.data instanceof FormData) config.data.set('token', token)
        if (String(config.method).toLowerCase() === 'get') {
          config.params = { ...config.params, token }
        }
      }
    }
    return config
  },
  error => {
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

service.interceptors.response.use(
  response => {
    const res = response.data
    if (!res || !Object.prototype.hasOwnProperty.call(res, 'code')) {
      return res
    }
    if (String(res.code) !== '200' && String(res.code) !== '0') {
      console.error('Response error:', res.msg || res.message || 'Request failed')
      const error = new Error(res.msg || res.message || 'Request failed')
      error.code = res.code
      return Promise.reject(error)
    }
    return res
  },
  error => {
    console.error('Response error:', error.message)
    return Promise.reject(error)
  }
)

export default service

export const user = {
  countries: () => service.get('/countries'),
  cities: (params) => service.get(`/cities/`, { params }),  
  addresses: (params) => service.get('/v2/member/mall/addresses', { params }),
  addressDetail: (addressId) => service.get(`/v2/member/mall/addresses/${addressId}`),
  addAddress: (data) => service.post('/v2/member/mall/addresses', toFormData(data)),
  updateAddress: (addressId, data) => service.put(
    `/v2/member/mall/addresses/${addressId}`,
    toFormData(data)
  ),
  setDefaultAddress: (addressId) => service.post(
    `/v2/member/mall/addresses/${addressId}/default`,
    toFormData()
  ),
  deleteAddress: (addressId) => service.delete(`/v2/member/mall/addresses/${addressId}`),
  orderStatusCounts: () => service.get('/v2/member/mall/order-status-counts'),
  cart: (params) => service.get('/v2/member/mall/cart', { params }),
  updateCartItem: (cartItemId, data) => service.put(
    `/v2/member/mall/cart/${cartItemId}`,
    toFormData(data)
  ),
  deleteCartItem: (cartItemId) => service.delete(`/v2/member/mall/cart/${cartItemId}`),
  deleteSelectedCartItems: (cartItemIds) => service.post(
    '/v2/member/mall/cart/delete-selected',
    toFormData({ cart_item_ids: cartItemIds })
  ),
  clearCart: () => service.post('/v2/member/mall/cart/clear', toFormData()),
  cartCount: () => service.get('/v2/member/mall/cart/count'),
  previewCheckout: (productJson) => service.post(
    '/v2/member/mall/cart/preview-checkout',
    toFormData({ productJson })
  ),
  mainOrders: (params) => service.get('/v2/member/mall/orders/main', { params }),
  mainOrderDetail: (mainOrderId, options = {}) => service.get(`/v2/member/mall/orders/main/${mainOrderId}`, {
    params: options.cacheBust ? { _: Date.now() } : {}
  }),
  submitMainOrder: (data) => service.post(
    '/v2/member/mall/orders/main/submit',
    toFormData(data)
  ),
  cancelMainOrder: (mainOrderId) => service.post(
    `/v2/member/mall/orders/main/${mainOrderId}/cancel`,
    toFormData()
  ),
  payMainOrderIntention: (mainOrderId, data) => service.post(
    `/v2/member/mall/orders/main/${mainOrderId}/pay-intention`,
    toFormData(data)
  ),
  payMainOrderDeposit: (mainOrderId, data) => service.post(
    `/v2/member/mall/orders/main/${mainOrderId}/pay-deposit`,
    toFormData(data)
  ),
  payMainOrderBalance: (mainOrderId, data) => service.post(
    `/v2/member/mall/orders/main/${mainOrderId}/pay-balance`,
    toFormData(data)
  ),
  confirmMainOrderReceipt: (mainOrderId) => service.post(
    `/v2/member/mall/orders/main/${mainOrderId}/confirm-receipt`,
    toFormData()
  ),
  deleteMainOrder: (mainOrderId) => service.delete(`/v2/member/mall/orders/main/${mainOrderId}`),
  accessoryOrders: (params) => service.get('/v2/member/mall/orders/accessory', { params }),
  accessoryOrderDetail: (accessoryOrderId, options = {}) => service.get(
    `/v2/member/mall/orders/accessory/${accessoryOrderId}`,
    { params: options.cacheBust ? { _: Date.now() } : {} }
  ),
  submitAccessoryOrder: async (data) => {
    const { orderData, paymentData } = splitAccessoryOrderPaymentData(data)
    const submitResponse = await service.post(
      '/v2/member/mall/orders/accessory/submit',
      toFormData(orderData)
    )
    const accessoryOrderId = getAccessoryOrderId(submitResponse)
    if (!accessoryOrderId) {
      throw new Error('Missing accessory order id')
    }
    const paymentResponse = await service.post(
      `/v2/member/mall/orders/accessory/${accessoryOrderId}/pay`,
      toFormData(paymentData)
    )
    return {
      ...submitResponse,
      payment: paymentResponse
    }
  },
  cancelAccessoryOrder: (accessoryOrderId) => service.post(
    `/v2/member/mall/orders/accessory/${accessoryOrderId}/cancel`,
    toFormData()
  ),
  payAccessoryOrder: (accessoryOrderId, data) => service.post(
    `/v2/member/mall/orders/accessory/${accessoryOrderId}/pay`,
    toFormData(data)
  ),
  confirmAccessoryOrderReceipt: (accessoryOrderId) => service.post(
    `/v2/member/mall/orders/accessory/${accessoryOrderId}/confirm-receipt`,
    toFormData()
  ),
  deleteAccessoryOrder: (accessoryOrderId) => service.delete(
    `/v2/member/mall/orders/accessory/${accessoryOrderId}`
  ),
  afterSales: (params) => service.get('/v2/member/mall/after-sales', { params }),
  applicableAfterSaleItems: (params) => service.get(
    '/v2/member/mall/after-sales/applicable-items',
    { params }
  ),
  afterSaleDetail: (afterSaleId) => service.get(`/v2/member/mall/after-sales/${afterSaleId}`),
  submitAfterSale: (data) => service.post('/v2/member/mall/after-sales', toFormData(data)),
  appendAfterSale: (afterSaleId, data) => service.post(
    `/v2/member/mall/after-sales/${afterSaleId}/append`,
    toFormData(data)
  ),
  cancelAfterSale: (afterSaleId) => service.post(
    `/v2/member/mall/after-sales/${afterSaleId}/cancel`,
    toFormData()
  ),
  pendingReviews: (params) => service.get('/v2/member/mall/reviews/pending', { params }),
  reviewHistory: (params) => service.get('/v2/member/mall/reviews/history', { params }),
  reviewDetail: (reviewId) => service.get(`/v2/member/mall/reviews/${reviewId}`),
  submitReview: (data) => service.post('/v2/member/mall/reviews', toFormData(data)),
  profile: () => service.get('/v2/member/profile'),
  updateProfile: (data) => service.post('/v2/member/profile', toFormData(data)),
  updateProfilePassword: (data) => service.post(
    '/v2/member/profile/password',
    toFormData(data)
  ),
  sendProfileEmailCode: (email) => service.post(
    '/v2/member/profile/email-code',
    toFormData({ email })
  ),
  updateProfileEmail: (data) => service.post('/v2/member/profile/email', toFormData(data)),
  updateProfileAvatar: (avatar) => service.post(
    '/v2/member/profile/avatar',
    toFormData({ avatar })
  )
}

export const work = {}
export const draft = {}
export const order = {}
export const upload = {
  image: (file) => service.post('/upload', toFormData({ file }))
}
